import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { CookieService } from '../cookie-service/cookie.service';
import { environment } from '../../../../environments/environment';

export interface NotificationData {
  id_notification: string;
  titre_fr: string;
  description_fr: string;
  priorite: string;
  code_type_objet: string;
  date_creation: string;
  type: string;
  unread_count?: number;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private connected = false;
  
  private notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private notificationReadBroadcastSubject = new BehaviorSubject<any>(null);
  private dropdownUpdateSubject = new BehaviorSubject<any>(null);

  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  constructor(private cookieService: CookieService) {}

  connect(): void {
    const token = this.getToken();
    
    if (!token) {
      console.warn('No authentication token found, cannot connect to socket');
      return;
    }

    if (this.socket && this.connected) {
      return;
    }

    const socketUrl = environment.socketUrl; 
    
    this.socket = io(socketUrl, {
      auth: {
        token: token
      },
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    this.setupEventListeners();
  }

  private getToken(): string | null {
    const cookieKeys = ['token', 'authToken', 'accessToken', 'jwt'];
    
    // Try cookie service first
    for (const key of cookieKeys) {
      try {
        const cookieData = this.cookieService.getData(key);
        if (cookieData) {
          if (typeof cookieData === 'object' && cookieData.token) {
            return cookieData.token;
          }
          if (typeof cookieData === 'string' && this.isValidJWT(cookieData)) {
            return cookieData;
          }
        }
      } catch (error) {
        continue;
      }
    }

    // Try localStorage and sessionStorage
    for (const key of cookieKeys) {
      const tokenFromLocal = localStorage.getItem(key);
      if (tokenFromLocal && this.isValidJWT(tokenFromLocal)) {
        return tokenFromLocal;
      }

      const tokenFromSession = sessionStorage.getItem(key);
      if (tokenFromSession && this.isValidJWT(tokenFromSession)) {
        return tokenFromSession;
      }
    }

    // Try direct cookie parsing
    const allCookies = this.getAllCookies();
    
    for (const [cookieName, cookieValue] of Object.entries(allCookies)) {
      if (this.isValidJWT(cookieValue)) {
        return cookieValue;
      }
      
      try {
        const parsed = JSON.parse(cookieValue);
        if (parsed.token && this.isValidJWT(parsed.token)) {
          return parsed.token;
        }
      } catch (e) {
        continue;
      }
    }

    return null;
  }

  private getAllCookies(): { [key: string]: string } {
    const cookies: { [key: string]: string } = {};
    
    if (!document.cookie) return cookies;
    
    document.cookie.split(';').forEach(cookie => {
      const [name, ...rest] = cookie.trim().split('=');
      if (name && rest.length > 0) {
        const value = rest.join('=');
        cookies[name] = decodeURIComponent(value);
      }
    });
    
    return cookies;
  }

  private isValidJWT(token: string): boolean {
    if (!token || typeof token !== 'string') return false;
    
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    
    if (token.length < 100) return false;
    
    if (!parts[0].startsWith('eyJ')) return false;
    
    return true;
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connected = true;
      this.connectionStatusSubject.next(true);
      
      // Request current data on connection
      this.socket?.emit('request_unread_count');
      this.socket?.emit('request_dropdown_notifications');
    });

    this.socket.on('disconnect', () => {
      this.connected = false;
      this.connectionStatusSubject.next(false);
    });

    // Handle new notifications
    this.socket.on('new_notification', (notification: NotificationData) => {
      this.handleNewNotification(notification);
    });

    // Handle unread count updates
    this.socket.on('unread_count_update', (data: { unread_count: number }) => {
      this.unreadCountSubject.next(data.unread_count);
    });

    // 🔔 CROSS-DEVICE SYNC: Handle notification marked as read on other devices
    this.socket.on('notification_read_broadcast', (data: any) => {
      this.notificationReadBroadcastSubject.next(data);
    });

    // 🔔 CROSS-DEVICE SYNC: Handle dropdown updates from server
    this.socket.on('dropdown_notifications_update', (data: any) => {
      this.dropdownUpdateSubject.next(data);
    });

    // Handle notification marked as read (local)
    this.socket.on('notification_marked_read', (data: { notificationId: string }) => {
      this.updateNotificationStatus(data.notificationId);
    });

    this.socket.on('connect_error', (error: any) => {
      this.connected = false;
      this.connectionStatusSubject.next(false);
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      this.connected = true;
      this.connectionStatusSubject.next(true);
      
      // Request current data on reconnection
      this.socket?.emit('request_unread_count');
      this.socket?.emit('request_dropdown_notifications');
    });

    this.socket.on('reconnect_failed', () => {
      // Reconnection failed
    });
  }

  private handleNewNotification(notification: NotificationData): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = [notification, ...currentNotifications];
    
    // Keep only latest 10 notifications in memory for dropdown
    if (updatedNotifications.length > 10) {
      updatedNotifications.splice(10);
    }
    
    this.notificationsSubject.next(updatedNotifications);
    this.showBrowserNotification(notification);
  }

  private updateNotificationStatus(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification => 
      notification.id_notification === notificationId 
        ? { ...notification, read: true }
        : notification
    );
    this.notificationsSubject.next(updatedNotifications);
  }

  private showBrowserNotification(notification: NotificationData): void {
    if ('Notification' in window && Notification.permission === 'granted') {
      const browserNotification = new Notification(notification.titre_fr, {
        body: notification.description_fr,
        icon: '/assets/images/logo.svg',
        badge: '/assets/images/logo.svg',
        tag: notification.id_notification,
        requireInteraction: false,
        silent: false
      });

      setTimeout(() => {
        browserNotification.close();
      }, 5000);

      browserNotification.onclick = () => {
        window.focus();
        browserNotification.close();
      };
    }
  }

  markNotificationAsRead(notificationId: string): void {
    if (this.socket && this.connected) {
      this.socket.emit('mark_notification_read', notificationId);
    }
  }

  openNotification(notificationId: string): void {
    if (this.socket && this.connected) {
      this.socket.emit('open_notification', notificationId);
    }
  }

  requestNotificationPermission(): void {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
      this.connectionStatusSubject.next(false);
    }
  }

  isConnected(): boolean {
    return this.connected;
  }

  getCurrentNotificationsCount(): number {
    return this.notificationsSubject.value.length;
  }

  forceReconnect(): void {
    this.disconnect();
    setTimeout(() => {
      this.connect();
    }, 1000);
  }

  // Request current unread count manually
  requestUnreadCount(): void {
    if (this.socket && this.connected) {
      this.socket.emit('request_unread_count');
    }
  }

  // 🔔 CROSS-DEVICE SYNC: Observable for notifications read on other devices
  onNotificationReadBroadcast(): Observable<any> {
    return this.notificationReadBroadcastSubject.asObservable();
  }

  // 🔔 CROSS-DEVICE SYNC: Observable for dropdown updates
  onDropdownUpdate(): Observable<any> {
    return this.dropdownUpdateSubject.asObservable();
  }
}